// src/components/HobbliHub.js

import React, { useRef, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useHubData from "./hooks/useHubData";
import "./HobbliHub.css";
import ItemSale from "./ItemSale";
import { supabase } from "../supabaseClient";
import { useEffect } from "react";
import ReactionBar from "./social/ReactionBar";
import useNotifications from "./hooks/useNotifications";
import CommentThread from "./social/CommentThread";
import {
  getNotificationDestination,
  getNotificationText,
} from "./NotificationPanel";
import usePublicItemSocialSummaries from "./social/usePublicItemSocialSummaries";
import PublicItemSocialPreview from "./social/PublicItemSocialPreview";
import formatRelativeTime from "./social/formatRelativeTime";
import { resolveImageUrl } from "../services/mediaUrl";

const PUBLIC_PAGE_SIZE = 12;
const FOR_SALE_PAGE_SIZE = 12;

export default function HobbliHub({ currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    forSale,
    forSaleLoading,
    forSaleHasMore,
    fetchForSale,

    publicFeed,
    publicLoading,
    publicHasMore,
    fetchPublicFeed,

    myFeed,
    connections,
    loading,
  } = useHubData(currentUser);

  const [selectedSale, setSelectedSale] = useState(null);
  const [selectedPublicItem, setSelectedPublicItem] = useState(null);
  const [uiNotice, setUiNotice] = useState(null);

  const [mobileView, setMobileView] = useState("public");

  const [selectedFriendId, setSelectedFriendId] = useState(null);

  /**
   * Protects the deep-link effect from immediately reopening
   * the public item while its URL parameters are being cleared.
   */
  const closingPublicItemRef = useRef(false);

  /**
   * ============================================================
   * NOTIFICATIONS
   * ============================================================
   */

  const {
    notifications,
    markAsRead,
  } = useNotifications(currentUser);

  /**
   * ============================================================
   * HUB DEEP-LINK PARAMETERS
   * ============================================================
   *
   * Supported:
   *
   * /hub?item=1080
   *
   * /hub?item=1080&comments=1
   *
   * /hub?item=1080&comment=<uuid>
   *
   * ============================================================
   */

  const query = useMemo(
    () =>
      new URLSearchParams(
        location.search
      ),
    [location.search]
  );

  const focusItemId =
    query.get("item");

  const focusCommentId =
    query.get("comment");

  const focusComments =
    query.get("comments") === "1";

  /**
   * ============================================================
   * OPEN PUBLIC ITEM
   * ============================================================
   */

  const openPublicItem = useCallback(
    (
      item,
      commentId = null
    ) => {
      if (!item?.id) {
        return;
      }

      setSelectedPublicItem(
        item
      );

      setMobileView(
        "public"
      );

      const params =
        new URLSearchParams(
          location.search
        );

      params.set(
        "item",
        item.id
      );

      if (commentId) {
        params.set(
          "comment",
          commentId
        );

        params.delete(
          "comments"
        );
      } else {
        params.delete(
          "comment"
        );

        params.delete(
          "comments"
        );
      }

      navigate(
        `/hub?${params.toString()}`,
        {
          replace: true,
        }
      );
    },
    [
      location.search,
      navigate,
    ]
  );

  /**
   * ============================================================
   * OPEN PUBLIC ITEM DIRECTLY TO COMMENTS
   * ============================================================
   */

  const openPublicItemComments = useCallback(
    (item) => {
      if (!item?.id) {
        return;
      }

      setSelectedPublicItem(
        item
      );

      setMobileView(
        "public"
      );

      const params =
        new URLSearchParams(
          location.search
        );

      params.set(
        "item",
        item.id
      );

      params.set(
        "comments",
        "1"
      );

      params.delete(
        "comment"
      );

      navigate(
        `/hub?${params.toString()}`,
        {
          replace: true,
        }
      );
    },
    [
      location.search,
      navigate,
    ]
  );

  /**
   * ============================================================
   * CLOSE PUBLIC ITEM
   * ============================================================
   */

  const closePublicItem =
    useCallback(() => {
      closingPublicItemRef.current =
        true;

      const params =
        new URLSearchParams(
          location.search
        );

      params.delete(
        "item"
      );

      params.delete(
        "comment"
      );

      params.delete(
        "comments"
      );

      const search =
        params.toString();

      navigate(
        search
          ? `/hub?${search}`
          : "/hub",
        {
          replace: true,
        }
      );

      setSelectedPublicItem(
        null
      );
    }, [
      location.search,
      navigate,
    ]);

  /**
   * ============================================================
   * FETCH PUBLIC ITEM BY ID
   * ============================================================
   *
   * Used when a notification opens an item that is not currently
   * present in the already-loaded public feed.
   * ============================================================
   */

  const fetchPublicItemById =
    useCallback(
      async (itemId) => {
        if (!itemId) {
          return null;
        }

        const {
          data: userItem,
          error: userItemError,
        } = await supabase
          .from(
            "user_items"
          )
          .select("*")
          .eq(
            "id",
            itemId
          )
          .maybeSingle();

        if (
          userItemError ||
          !userItem
        ) {
          if (
            userItemError
          ) {
            console.error(
              "Failed to open notification item:",
              userItemError
            );
          }

          return null;
        }

        const [
          itemResult,
          imageResult,
          userResult,
        ] =
          await Promise.all([
            userItem.item_id
              ? supabase
                  .from(
                    "items"
                  )
                  .select("*")
                  .eq(
                    "id",
                    userItem.item_id
                  )
                  .maybeSingle()
              : Promise.resolve({
                  data: null,
                }),

            supabase
              .from(
                "item_images"
              )
              .select("*")
              .eq(
                "user_item_id",
                userItem.id
              )
              .order(
                "position",
                {
                  ascending: true,
                }
              ),

            userItem.user_id
              ? supabase
                  .rpc("get_user_profiles", {
                    p_user_ids: [userItem.user_id],
                  })
                  .then(({ data, error }) => ({
                    data: data?.[0] || null,
                    error,
                  }))
              : Promise.resolve({
                  data: null,
                  error: null,
                }),
          ]);

        return {
          ...userItem,

          items:
            itemResult.data ||
            null,

          item_images:
            imageResult.data ||
            [],

          users:
            userResult.data ||
            null,
        };
      },
      []
    );

  /**
   * ============================================================
   * OPEN ITEM FROM URL
   * ============================================================
   */

  useEffect(() => {
    let active = true;

    /**
     * We intentionally just closed the modal.
     *
     * Wait for the item query parameter to disappear before
     * allowing the deep-link effect to open anything again.
     */
    if (
      closingPublicItemRef.current
    ) {
      if (
        !focusItemId
      ) {
        closingPublicItemRef.current =
          false;
      }

      return undefined;
    }

    if (
      !focusItemId
    ) {
      return undefined;
    }

    const alreadySelected =
      selectedPublicItem &&
      String(
        selectedPublicItem.id
      ) ===
        String(
          focusItemId
        );

    if (
      alreadySelected
    ) {
      return undefined;
    }

    /**
     * First try the currently loaded feed.
     */
    const loaded =
      (
        publicFeed ||
        []
      ).find(
        (item) =>
          String(
            item.id
          ) ===
          String(
            focusItemId
          )
      );

    if (
      loaded
    ) {
      setSelectedPublicItem(
        loaded
      );

      setMobileView(
        "public"
      );

      return undefined;
    }

    /**
     * Otherwise load the item directly.
     */
    fetchPublicItemById(
      focusItemId
    ).then(
      (item) => {
        if (
          active &&
          item
        ) {
          setSelectedPublicItem(
            item
          );

          setMobileView(
            "public"
          );
        }
      }
    );

    return () => {
      active = false;
    };
  }, [
    focusItemId,
    publicFeed,
    selectedPublicItem,
    fetchPublicItemById,
  ]);

  /**
   * ============================================================
   * SCROLL TO COMMENTS
   * ============================================================
   */

  useEffect(() => {
    if (
      !selectedPublicItem ||
      !focusComments
    ) {
      return undefined;
    }

    let frameTwo =
      null;

    const frameOne =
      window.requestAnimationFrame(
        () => {
          frameTwo =
            window.requestAnimationFrame(
              () => {
                document
                  .getElementById(
                    `comments-${String(
                      selectedPublicItem.id
                    )}`
                  )
                  ?.scrollIntoView(
                    {
                      behavior:
                        "smooth",

                      block:
                        "start",
                    }
                  );
              }
            );
        }
      );

    return () => {
      window.cancelAnimationFrame(
        frameOne
      );

      if (
        frameTwo !==
        null
      ) {
        window.cancelAnimationFrame(
          frameTwo
        );
      }
    };
  }, [
    selectedPublicItem,
    focusComments,
  ]);

  /**
   * ============================================================
   * HUB NOTIFICATION CLICK
   * ============================================================
   */

  const handleHubNotificationClick =
    useCallback(
      async (
        notification
      ) => {
        if (
          !notification
        ) {
          return;
        }

        if (
          !notification.is_read
        ) {
          await markAsRead(
            notification.id
          );
        }

        const destination =
          getNotificationDestination(
            notification
          );

        if (
          !destination
        ) {
          return;
        }

        navigate(
          destination
        );
      },
      [
        markAsRead,
        navigate,
      ]
    );

  /**
   * ============================================================
   * SALE MAPPING
   * ============================================================
   */

  const mapSaleToCard =
    (listing) => {
      return {
        id:
          listing.listingId,

        listing,

        card: {
          id:
            listing.listingId,

          listingId:
            listing.listingId,

          name:
            listing.title ||
            "Unknown Item",

          brand:
            listing.brand ||
            "",

          set_name:
            listing.setName ||
            "",

          year:
            listing.year ||
            "",

          condition:
            listing.condition ||
            "",

          owner:
            listing.shop?.name ||
            listing.seller?.displayName ||
            "Seller",

          images:
            listing.images ||
            [],

          attributes:
            listing.attributes ||
            {},

          sale: {
            price:
              listing.priceAmount,

            acceptOffers:
              listing.acceptsOffers,

            minimumOffer:
              listing.minimumOfferAmount,

            description:
              listing.description,

            sellerId:
              listing.seller?.contactUserId ||
              null,

            sellerAccountId:
              listing.seller?.accountId ||
              null,

            shopId:
              listing.shop?.id ||
              null,

            shopName:
              listing.shop?.name ||
              "",

            isPlatformShop:
              Boolean(
                listing.shop?.isPlatformShop
              ),

            quantityAvailable:
              listing.quantityAvailable ??
              0,
          },
        },
      };
    };

  const mappedForSale =
    useMemo(
      () => {
        return (
          forSale ||
          []
        ).map(
          mapSaleToCard
        );
      },
      [
        forSale,
      ]
    );

  /**
   * ============================================================
   * INCREMENTAL RENDERING
   * ============================================================
   */

  const [
    visibleForSale,
    setVisibleForSale,
  ] = useState(
    FOR_SALE_PAGE_SIZE
  );

  const [
    visiblePublic,
    setVisiblePublic,
  ] = useState(
    PUBLIC_PAGE_SIZE
  );

  /**
   * ============================================================
   * PUBLIC FEED FILTERING
   * ============================================================
   */

  const dedupedPublicFeed =
    publicFeed ||
    [];

  const filteredPublicFeed =
    useMemo(
      () => {
        if (
          !selectedFriendId
        ) {
          return (
            dedupedPublicFeed
          );
        }

        return (
          dedupedPublicFeed.filter(
            (item) =>
              item.user_id ===
              selectedFriendId
          )
        );
      },
      [
        dedupedPublicFeed,
        selectedFriendId,
      ]
    );

  /**
   * Independent viewport sentinels. Each feed advances when its own
   * last rendered card approaches the viewport, not when the longest
   * column reaches the bottom of the page.
   */
  const marketplaceDesktopSentinelRef = useRef(null);
  const marketplaceMobileSentinelRef = useRef(null);
  const publicDesktopSentinelRef = useRef(null);
  const publicMobileSentinelRef = useRef(null);
  const marketplaceRequestRef = useRef(false);
  const publicRequestRef = useRef(false);

  useEffect(() => {
    if (loading || forSaleLoading) return undefined;
    const nodes = [marketplaceDesktopSentinelRef.current, marketplaceMobileSentinelRef.current].filter(Boolean);
    if (!nodes.length) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      if (visibleForSale < mappedForSale.length) {
        setVisibleForSale((count) => Math.min(count + FOR_SALE_PAGE_SIZE, mappedForSale.length));
      } else if (forSaleHasMore && !marketplaceRequestRef.current) {
        marketplaceRequestRef.current = true;
        Promise.resolve().then(() => fetchForSale()).catch((error) => {
          console.error("Failed to load marketplace listings:", error);
        }).finally(() => { marketplaceRequestRef.current = false; });
      }
    }, { root: null, rootMargin: "400px 0px", threshold: 0 });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [loading, forSaleLoading, visibleForSale, mappedForSale.length, forSaleHasMore, fetchForSale, mobileView]);

  useEffect(() => {
    if (loading || publicLoading) return undefined;
    const nodes = [publicDesktopSentinelRef.current, publicMobileSentinelRef.current].filter(Boolean);
    if (!nodes.length) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      if (visiblePublic < filteredPublicFeed.length) {
        setVisiblePublic((count) => Math.min(count + PUBLIC_PAGE_SIZE, filteredPublicFeed.length));
      } else if (!selectedFriendId && publicHasMore && !publicRequestRef.current) {
        publicRequestRef.current = true;
        Promise.resolve().then(() => fetchPublicFeed()).catch((error) => {
          console.error("Failed to load public collection cards:", error);
        }).finally(() => { publicRequestRef.current = false; });
      }
    }, { root: null, rootMargin: "400px 0px", threshold: 0 });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [loading, publicLoading, visiblePublic, filteredPublicFeed.length, publicHasMore, fetchPublicFeed, selectedFriendId, mobileView]);

  useEffect(() => {
    setVisiblePublic(PUBLIC_PAGE_SIZE);
  }, [selectedFriendId]);

  /**
   * ============================================================
   * VISIBLE FEED ITEMS
   * ============================================================
   */

  const visibleMarketplaceItems =
    useMemo(
      () => {
        return mappedForSale.slice(
          0,
          visibleForSale
        );
      },
      [
        mappedForSale,
        visibleForSale,
      ]
    );

  const visiblePublicItems =
    useMemo(
      () => {
        return filteredPublicFeed.slice(
          0,
          visiblePublic
        );
      },
      [
        filteredPublicFeed,
        visiblePublic,
      ]
    );

  /**
   * ============================================================
   * BATCHED PUBLIC SOCIAL SUMMARY
   * ============================================================
   */

  const visiblePublicItemIds =
    useMemo(
      () =>
        visiblePublicItems.map(
          (
            item
          ) =>
            item.id
        ),
      [
        visiblePublicItems,
      ]
    );

  const {
    getSummary:
      getPublicItemSocialSummary,

    refresh:
      refreshPublicItemSocialSummaries,
  } =
    usePublicItemSocialSummaries(
      visiblePublicItemIds
    );

  /**
   * ============================================================
   * MESSAGE SELLER
   * ============================================================
   */

  const sendSellerMessage =
    async (
      card
    ) => {
      const {
        data: {
          session,
        },
      } =
        await supabase.auth.getSession();

      const GUEST_USER_ID =
        "5ce4c73c-9222-477c-8308-4bff6e920370";

      const senderId =
        session?.user?.id ??
        GUEST_USER_ID;

      const firstName =
        document
          .getElementById(
            "firstName"
          )
          ?.value
          ?.trim();

      const lastName =
        document
          .getElementById(
            "lastName"
          )
          ?.value
          ?.trim();

      const email =
        document
          .getElementById(
            "email"
          )
          ?.value
          ?.trim();

      const locationValue =
        document
          .getElementById(
            "location"
          )
          ?.value
          ?.trim();

      const messageText =
        document
          .getElementById(
            "message"
          )
          ?.value
          ?.trim();

      const offerAmount =
        card.sale.acceptOffers
          ? document
              .getElementById(
                "offerAmount"
              )
              ?.value
              ?.trim()
          : null;

      if (
        !firstName ||
        !lastName ||
        !email
      ) {
        setUiNotice({ type: "error", message: "Please fill in First Name, Last Name, and Email." });

        return;
      }

      const fullMessage = `
A buyer is interested in your item.

ITEM:
- ${card.year ? `${card.year} - ` : ""}${card.name}
- Set: ${card.set_name || "N/A"}
- Price: $${card.sale.price}

BUYER INFORMATION:
- ${firstName} ${lastName}
- Email: ${email}
- Location: ${locationValue || "Not provided"}

${offerAmount ? `OFFER: $${offerAmount}\n` : ""}

MESSAGE:
${messageText || "(No message)"}
`.trim();

      const {
        error,
      } =
        await supabase
          .from(
            "messages"
          )
          .insert({
            sender_id:
              senderId,

            receiver_id:
              card.sale
                .sellerId,

            content:
              fullMessage,

            metadata: {
              first_name:
                firstName,

              last_name:
                lastName,

              email,

              city_state:
                locationValue,

              offer_amount:
                offerAmount
                  ? Number(
                      offerAmount
                    )
                  : null,

              item_id:
                card.id,

              item_name:
                card.name,

              set_name:
                card.set_name,

              year:
                card.year,
            },
          });

      if (
        error
      ) {
        console.error(
          "Error sending message:",
          error
        );

        setUiNotice({ type: "error", message: "Could not send message. Please try again." });

        return;
      }

      setUiNotice({ type: "success", message: "Your message has been sent!" });

      setSelectedSale(
        null
      );
    };

  /**
   * ============================================================
   * MARKETPLACE SKELETON
   * ============================================================
   */

  const showMarketplaceSkeleton =
    !forSale?.length &&
    forSaleLoading;

  /**
   * ============================================================
   * SORTED CONNECTIONS
   * ============================================================
   */

  const sortedConnections =
    useMemo(
      () => {
        return [
          ...(
            connections ||
            []
          ),
        ].sort(
          (
            a,
            b
          ) => {
            const nameA =
              a.display_name
                ?.toLowerCase() ||
              "";

            const nameB =
              b.display_name
                ?.toLowerCase() ||
              "";

            return nameA.localeCompare(
              nameB
            );
          }
        );
      },
      [
        connections,
      ]
    );

  const selectedFriend =
    useMemo(
      () => {
        return sortedConnections.find(
          (
            friend
          ) =>
            friend.id ===
            selectedFriendId
        );
      },
      [
        sortedConnections,
        selectedFriendId,
      ]
    );

  /**
   * ============================================================
   * SMALL NOTIFICATION ROW
   * ============================================================
   */

  const renderNotification =
    (
      notification
    ) => {
      const destination =
        getNotificationDestination(
          notification
        );

      const isMessage =
        String(
          notification?.type ||
            notification
              ?.notification_type ||
            ""
        )
          .toLowerCase()
          .includes(
            "message"
          );

      return (
        <button
          type="button"
          className={`hub-activity-item hub-notification-row ${
            notification.is_read
              ? ""
              : "unread"
          }`}
          key={
            notification.id
          }
          onClick={() =>
            handleHubNotificationClick(
              notification
            )
          }
        >
          <span className="hub-notification-copy">
            <span className="hub-notification-text">
              {getNotificationText(
                notification
              )}
            </span>

            <span className="hub-notification-meta">
              {formatRelativeTime(
                notification.created_at
              )}

              {destination && (
                <>
                  <span className="hub-notification-dot">
                    •
                  </span>

                  <span className="hub-notification-action">
                    {isMessage
                      ? "Open message"
                      : "View item"}
                  </span>
                </>
              )}
            </span>
          </span>
        </button>
      );
    };

  /**
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div
      className="hub-container"
    >
      {uiNotice ? (
        <div role="status" style={{ margin: "10px 12px", padding: "10px 12px", borderRadius: "8px", background: uiNotice.type === "error" ? "rgba(220,53,69,.10)" : "rgba(47,128,237,.10)", color: uiNotice.type === "error" ? "#b42318" : "inherit", fontSize: ".88rem" }}>{uiNotice.message}</div>
      ) : null}

      {/* =======================================================
          MOBILE
      ======================================================= */}

      <div className="hub-mobile-top">
        <div className="hub-mobile-tabs">
          <button
            className={
              mobileView ===
              "public"
                ? "active"
                : ""
            }
            onClick={() =>
              setMobileView(
                "public"
              )
            }
          >
            Public
          </button>

          <button
            className={
              mobileView ===
              "marketplace"
                ? "active"
                : ""
            }
            onClick={() =>
              setMobileView(
                "marketplace"
              )
            }
          >
            Marketplace
          </button>

          <button
            className={
              mobileView ===
              "social"
                ? "active"
                : ""
            }
            onClick={() =>
              setMobileView(
                "social"
              )
            }
          >
            Social
          </button>
        </div>

        {/* =====================================================
            MOBILE MARKETPLACE
        ===================================================== */}

        {mobileView ===
          "marketplace" && (
          <div className="hub-mobile-sale-scroll">
            <h3>
              Marketplace
            </h3>

            <div className="hub-scroll-row">
              {showMarketplaceSkeleton ? (
                Array.from(
                  {
                    length: 3,
                  }
                ).map(
                  (
                    _,
                    index
                  ) => (
                    <div
                      key={
                        index
                      }
                      className="hub-sale-card compact skeleton"
                    />
                  )
                )
              ) : (
                visibleMarketplaceItems.map(
                  (
                    cardObj,
                    index
                  ) => (
                    <div
                      key={`mobile-sale-${cardObj.id}-${index}`}
                      className="hub-sale-card compact"
                      onClick={() =>
                        setSelectedSale(
                          cardObj.card
                        )
                      }
                    >
                      <div className="hub-thumb-wrapper">
                        <img
                          src={
                            cardObj
                              .card
                              .images
                              ?.[0]
                          }
                          alt={
                            cardObj
                              .card
                              .name
                          }
                          loading="lazy"
                        />
                      </div>

                      <div className="hub-card-meta">
                        <div className="hub-card-line top">
                          {
                            cardObj
                              .card
                              .year
                          }{" "}
                          {
                            cardObj
                              .card
                              .brand
                          }
                        </div>

                        {cardObj
                          .card
                          .set_name && (
                          <div className="hub-card-line set">
                            {
                              cardObj
                                .card
                                .set_name
                            }
                          </div>
                        )}

                        <div className="hub-card-name">
                          {
                            cardObj
                              .card
                              .name
                          }
                        </div>

                        <div className="hub-owner">
                          @
                          {
                            cardObj
                              .card
                              .owner
                          }
                        </div>

                        <div className="hub-sale-price">
                          $
                          {
                            cardObj
                              .card
                              .sale
                              .price
                          }
                        </div>
                      </div>
                    </div>
                  )
                )
              )}

              <div ref={marketplaceMobileSentinelRef} className="hub-feed-sentinel" aria-hidden="true" />
              {forSaleLoading && (
                <p className="hub-loading-more">
                  Loading...
                </p>
              )}
            </div>
          </div>
        )}

        {/* =====================================================
            MOBILE PUBLIC COLLECTIONS
        ===================================================== */}

        {mobileView ===
          "public" && (
          <div className="hub-mobile-public">
            <h3>
              Public Collections
            </h3>

            {selectedFriendId && (
              <div className="hub-filter-banner">
                Showing @
                {selectedFriend?.display_name || "collector"}

                <button
                  onClick={() =>
                    setSelectedFriendId(
                      null
                    )
                  }
                >
                  Show All
                </button>
              </div>
            )}

            <div className="hub-scroll-column">
              {visiblePublicItems.map(
                (
                  item
                ) => {
                  const image = resolveImageUrl(
                    item.item_images?.[0]
                  );

                  const itemName =
                    item.user_specific_name ||
                    item.items?.name ||
                    "Unknown Item";

                  const username =
                    item.users?.display_name ||
                    "collector";

                  return (
                    <div
                      key={`public-mobile-${item.id}`}
                      className="hub-feed-card compact"
                      onClick={() =>
                        openPublicItem(
                          item
                        )
                      }
                    >
                      {image && (
                        <div className="hub-thumb-wrapper">
                          <img
                            src={
                              image
                            }
                            alt={
                              itemName
                            }
                            loading="lazy"
                          />
                        </div>
                      )}

                      <div className="hub-card-meta">
                        <div className="hub-card-line top">
                          {[
                            item.user_specific_year ||
                              item
                                .items
                                ?.year,

                            item.user_specific_brand ||
                              item
                                .items
                                ?.brand,
                          ]
                            .filter(
                              Boolean
                            )
                            .join(
                              " • "
                            )}
                        </div>

                        {(item.user_specific_set_name ||
                          item.items
                            ?.set_name) && (
                          <div className="hub-card-line set">
                            {item.user_specific_set_name ||
                              item
                                .items
                                ?.set_name}
                          </div>
                        )}

                        <div className="hub-card-name">
                          {
                            itemName
                          }
                        </div>

                        <PublicItemSocialPreview
                          summary={getPublicItemSocialSummary(
                            item.id
                          )}
                          onOpenItem={() =>
                            openPublicItem(
                              item
                            )
                          }
                          onOpenComments={() =>
                            openPublicItemComments(
                              item
                            )
                          }
                        />

                        <div className="hub-owner">
                          Collected by @
                          {
                            username
                          }
                        </div>
                      </div>
                    </div>
                  );
                }
              )}

              <div ref={publicMobileSentinelRef} className="hub-feed-sentinel" aria-hidden="true" />
              {publicLoading && (
                <p className="hub-loading-more">
                  Loading
                  feed...
                </p>
              )}
            </div>
          </div>
        )}

        {/* =====================================================
            MOBILE SOCIAL
        ===================================================== */}

        {mobileView ===
          "social" && (
          <div className="hub-mobile-social">
            <h3>
              Social
            </h3>

            <div className="hub-social-panel">
              <div className="hub-social-box">
                <h4>
                  Friends (
                  {
                    sortedConnections.length
                  }
                  )
                </h4>

                {!sortedConnections.length ? (
                  <p className="hub-social-empty">
                    No connections
                    yet.
                  </p>
                ) : (
                  <div className="hub-friends-list">
                    {sortedConnections.map(
                      (
                        friend
                      ) => {
                        const displayName =
                          friend.display_name ||
                          "collector";

                        return (
                          <button
                            key={
                              friend.id
                            }
                            className={`hub-friend-item ${
                              selectedFriendId ===
                              friend.id
                                ? "active"
                                : ""
                            }`}
                            onClick={() => {
                              setSelectedFriendId(
                                friend.id
                              );

                              if (
                                window.innerWidth <
                                768
                              ) {
                                setMobileView(
                                  "public"
                                );
                              }
                            }}
                          >
                            @
                            {
                              displayName
                            }
                          </button>
                        );
                      }
                    )}
                  </div>
                )}
              </div>

              <div className="hub-social-box">
                <h4>
                  Notifications
                </h4>

                {notifications
                  .slice(
                    0,
                    10
                  )
                  .map(
                    renderNotification
                  )}

                {!notifications.length && (
                  <p className="hub-social-empty">
                    No notifications
                    yet.
                  </p>
                )}
              </div>

              <div className="hub-social-box">
                <h4>
                  Your Activity
                </h4>

                {myFeed?.length ? (
                  myFeed
                    .slice(
                      0,
                      5
                    )
                    .map(
                      (
                        post
                      ) => (
                        <div
                          key={`myfeed-${post.id}`}
                          className="hub-activity-item"
                        >
                          {post.title ||
                            "Collection update"}
                        </div>
                      )
                    )
                ) : (
                  <p className="hub-social-empty">
                    No recent
                    activity.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =======================================================
          DESKTOP
      ======================================================= */}

      <div className="hub-desktop">
        {/* =====================================================
            LEFT COLUMN - MARKETPLACE
        ===================================================== */}

        <div className="hub-col">
          <h3>
            Marketplace
          </h3>

          {visibleMarketplaceItems.map(
            (
              cardObj,
              index
            ) => (
              <div
                key={`desktop-sale-${cardObj.id}-${index}`}
                className="hub-sale-card compact"
                onClick={() =>
                  setSelectedSale(
                    cardObj.card
                  )
                }
              >
                <div className="hub-thumb-wrapper">
                  <img
                    src={
                      cardObj
                        .card
                        .images
                        ?.[0]
                    }
                    alt={
                      cardObj
                        .card
                        .name
                    }
                    loading="lazy"
                  />
                </div>

                <div className="hub-card-meta">
                  <div className="hub-card-line top">
                    {
                      cardObj
                        .card
                        .year
                    }{" "}
                    {
                      cardObj
                        .card
                        .brand
                    }
                  </div>

                  {cardObj
                    .card
                    .set_name && (
                    <div className="hub-card-line set">
                      {
                        cardObj
                          .card
                          .set_name
                      }
                    </div>
                  )}

                  <div className="hub-card-name">
                    {
                      cardObj
                        .card
                        .name
                    }
                  </div>

                  <div className="hub-owner">
                    @
                    {
                      cardObj
                        .card
                        .owner
                    }
                  </div>

                  <div className="hub-sale-price">
                    $
                    {
                      cardObj
                        .card
                        .sale
                        .price
                    }
                  </div>
                </div>
              </div>
            )
          )}

          {forSaleLoading && (
            <p className="hub-loading-more">
              Loading more
              items...
            </p>
          )}
          <div ref={marketplaceDesktopSentinelRef} className="hub-feed-sentinel" aria-hidden="true" />
        </div>

        {/* =====================================================
            MIDDLE COLUMN - PUBLIC COLLECTIONS
        ===================================================== */}

        <div className="hub-col">
          <h3>
            Public Collections
          </h3>

          {selectedFriendId && (
            <div className="hub-filter-banner">
              Showing items
              from @
              {selectedFriend?.display_name || "collector"}

              <button
                onClick={() =>
                  setSelectedFriendId(
                    null
                  )
                }
              >
                Show All
              </button>
            </div>
          )}

          <div className="hub-feed-grid">
            {visiblePublicItems.map(
              (
                item
              ) => {
                const image = resolveImageUrl(
                  item.item_images?.[0]
                );

                const itemName =
                  item.user_specific_name ||
                  item.items?.name ||
                  "Unknown Item";

                const brand =
                  item.user_specific_brand ||
                  item.items
                    ?.brand ||
                  "";

                const setName =
                  item.user_specific_set_name ||
                  item.items
                    ?.set_name ||
                  "";

                const year =
                  item.user_specific_year ||
                  item.items
                    ?.year ||
                  "";

                const username =
                  item.users?.display_name ||
                  "collector";

                return (
                  <div
                    key={`public-${item.id}`}
                    className="hub-feed-card compact"
                    onClick={() =>
                      openPublicItem(
                        item
                      )
                    }
                  >
                    {image && (
                      <div className="hub-thumb-wrapper">
                        <img
                          src={
                            image
                          }
                          alt={
                            itemName
                          }
                          loading="lazy"
                        />
                      </div>
                    )}

                    <div className="hub-card-meta">
                      <div className="hub-card-line top">
                        {[
                          year,
                          brand,
                        ]
                          .filter(
                            Boolean
                          )
                          .join(
                            " • "
                          )}
                      </div>

                      {setName && (
                        <div className="hub-card-line set">
                          {
                            setName
                          }
                        </div>
                      )}

                      <div className="hub-card-name">
                        {
                          itemName
                        }
                      </div>

                      <PublicItemSocialPreview
                        summary={getPublicItemSocialSummary(
                          item.id
                        )}
                        onOpenItem={() =>
                          openPublicItem(
                            item
                          )
                        }
                        onOpenComments={() =>
                          openPublicItemComments(
                            item
                          )
                        }
                      />

                      <div className="hub-owner">
                        Collected by @
                        {
                          username
                        }
                      </div>
                    </div>
                  </div>
                );
              }
            )}

            {publicLoading && (
              <p className="hub-loading-more">
                Loading
                feed...
              </p>
            )}
          </div>
          <div ref={publicDesktopSentinelRef} className="hub-feed-sentinel" aria-hidden="true" />
        </div>

        {/* =====================================================
            RIGHT COLUMN - SOCIAL
        ===================================================== */}

        <div className="hub-col">
          <h3>
            Social
          </h3>

          <div className="hub-social-panel">
            <div className="hub-social-box">
              <h4>
                Notifications
              </h4>

              {notifications
                .slice(
                  0,
                  10
                )
                .map(
                  renderNotification
                )}

              {!notifications.length && (
                <p className="hub-social-empty">
                  No notifications
                  yet.
                </p>
              )}
            </div>

            <div className="hub-social-box">
              <h4>
                Friends (
                {
                  sortedConnections.length
                }
                )
              </h4>

              {!sortedConnections.length ? (
                <p className="hub-social-empty">
                  No connections
                  yet.
                </p>
              ) : (
                <div className="hub-friends-list">
                  {sortedConnections.map(
                    (
                      friend
                    ) => {
                      const displayName =
                        friend.display_name ||
                        "collector";

                      return (
                        <button
                          key={
                            friend.id
                          }
                          className={`hub-friend-item ${
                            selectedFriendId ===
                            friend.id
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            setSelectedFriendId(
                              friend.id
                            );

                            if (
                              window.innerWidth <
                              768
                            ) {
                              setMobileView(
                                "public"
                              );
                            }
                          }}
                        >
                          @
                          {
                            displayName
                          }
                        </button>
                      );
                    }
                  )}
                </div>
              )}
            </div>

            <div className="hub-social-box">
              <h4>
                Your Activity
              </h4>

              {myFeed?.length ? (
                myFeed
                  .slice(
                    0,
                    5
                  )
                  .map(
                    (
                      post
                    ) => (
                      <div
                        key={`myfeed-${post.id}`}
                        className="hub-activity-item"
                      >
                        {post.title ||
                          "Collection update"}
                      </div>
                    )
                  )
              ) : (
                <p className="hub-social-empty">
                  No recent
                  activity.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* =======================================================
          SALE MODAL
      ======================================================= */}

      {selectedSale && (
        <ItemSale
          card={
            selectedSale
          }
          onClose={() =>
            setSelectedSale(
              null
            )
          }
          onSendMessage={() =>
            sendSellerMessage(
              selectedSale
            )
          }
        />
      )}

      {/* =======================================================
          PUBLIC ITEM MODAL
      ======================================================= */}

      {selectedPublicItem && (
        <div
          className="hub-social-modal-overlay"
          onClick={
            closePublicItem
          }
        >
          <div
            className="hub-social-modal"
            onClick={(
              event
            ) =>
              event.stopPropagation()
            }
          >
            <div className="hub-modal-handle" />

            <button
              className="hub-modal-close"
              onClick={
                closePublicItem
              }
            >
              ✕
            </button>

            {/* IMAGE */}

            {resolveImageUrl(
              selectedPublicItem.item_images?.[0]
            ) && (
              <img
                className="hub-social-modal-image"
                src={resolveImageUrl(
                  selectedPublicItem.item_images?.[0]
                )}
                alt={
                  selectedPublicItem.user_specific_name ||
                  selectedPublicItem.items?.name ||
                  "Collectible"
                }
              />
            )}

            {/* META */}

            <div className="hub-social-modal-content">
              <div className="hub-social-year-brand">
                {selectedPublicItem.user_specific_year ||
                  selectedPublicItem
                    .items
                    ?.year}{" "}
                {selectedPublicItem.user_specific_brand ||
                  selectedPublicItem
                    .items
                    ?.brand}
              </div>

              {(selectedPublicItem.user_specific_set_name ||
                selectedPublicItem
                  .items
                  ?.set_name) && (
                <div className="hub-social-set">
                  {selectedPublicItem.user_specific_set_name ||
                    selectedPublicItem
                      .items
                      ?.set_name}
                </div>
              )}

              <h2>
                {selectedPublicItem.user_specific_name ||
                  selectedPublicItem
                    .items
                    ?.name}
              </h2>

              {/* =================================================
                  REACTIONS
              ================================================= */}

              <ReactionBar
                targetType="user_item"
                targetId={
                  selectedPublicItem.id
                }
                ownerId={
                  selectedPublicItem.user_id
                }
                currentUser={
                  currentUser
                }
                onChange={
                  refreshPublicItemSocialSummaries
                }
              />

              <p className="hub-social-owner">
                Owned by @
                {selectedPublicItem
                  .users
                  ?.display_name ||
                  "collector"}
              </p>

              {selectedPublicItem.condition && (
                <p className="hub-social-condition">
                  Condition:{" "}
                  {
                    selectedPublicItem.condition
                  }
                </p>
              )}

              {/* =================================================
                  COMMENTS
              ================================================= */}

              <div
                id={`comments-${String(
                  selectedPublicItem.id
                )}`}
                className="hub-comments-anchor"
              >
                <CommentThread
                  targetType="user_item"
                  targetId={
                    selectedPublicItem.id
                  }
                  currentUser={
                    currentUser
                  }
                  focusCommentId={
                    focusCommentId
                  }
                  onChange={
                    refreshPublicItemSocialSummaries
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}